import { useEffect, useState } from "react";
import { useApi, ApiError } from "./api";
import type { ApiClient } from "./api";

// ─────────── Types matching backend response shapes ───────────

export interface AdminUserSummary {
  userId: string;
  email: string;
  name: string;
  role: "member" | "admin";
  lastSignInAt: number | null;
  online: boolean;
  onboardingCompletedAt: number | null;
  emailVerifiedAt: number | null;
  locked: boolean;
}

export interface AdminUserDetail {
  user: {
    userId: string;
    email: string;
    name: string;
    firstName: string | null;
    lastName: string | null;
    createdAt: number;
    role: "member" | "admin";
    lastSignInAt: number | null;
    locked: boolean;
    onboardingCompletedAt: number | null;
    emailVerifiedAt: number | null;
  };
  courses: Array<{
    courseSlug: string;
    courseTitle: string;
    progress: {
      courseSlug: string;
      startedAt: number;
      lastVisited: { chapter: string; lesson: string; ts: number } | null;
      completedLessons: string[];
    };
    /** Practice activity (CF-10): try.run rollup for this course. Null when
     *  the user has never run a try cell in it — render a "No practice
     *  recorded" badge. Rows predate the fix that wires telemetry into plain
     *  Python try cells, so pre-fix activity undercounts Python practice
     *  specifically. */
    practiceActivity: {
      totalRuns: number;
      distinctCells: number;
      lastRunAt: number | null;
    } | null;
  }>;
  coachNote: {
    title: string;
    bodyMarkdown: string;
    updatedAt: number;
    updatedBy: string | null;
  } | null;
  defaultCoachNote: { title: string; bodyMarkdown: string } | null;
  activity: {
    lessonsOpenedCount: number;
    lessonsCompletedCount: number;
    chaptersCompletedCount: number;
    lessonsCompletedThisWeek: number;
    daysActive7d: number;
    daysActive30d: number;
    firstSeen: number | null;
    lastSeen: number | null;
    recent: Array<{ ts: number; type: string; courseSlug?: string; chapter?: string; lesson?: string }>;
  };
  presence: {
    lastBeatAt: number;
    courseSlug: string | null;
    lastChapter: string | null;
    lastLesson: string | null;
  } | null;
  events: Array<{ ts: number; type: string; courseSlug?: string; chapter?: string; lesson?: string }>;
  portalOnboarding: {
    submittedAt: number;
    fieldIds: string[];
    responses: Record<string, string | string[]>;
  } | null;
}

export interface AdminWorkItem {
  email: string;
  courseSlug: string;
  chapter: string;
  lesson: string | null;
  type: "exercise" | "capstone";
  content: string;
  savedAt: number;
}

// ─────────── URL builders ───────────

export function adminUsersUrl(): string {
  return "/api/admin/users";
}

export function adminUserDetailUrl(userId: string): string {
  return `/api/admin/users/${encodeURIComponent(userId)}`;
}

export function adminResetProgressUrl(userId: string, courseSlug: string): string {
  return `/api/admin/users/${encodeURIComponent(userId)}/progress?courseSlug=${encodeURIComponent(courseSlug)}`;
}

export function adminUserWorkUrl(userId: string): string {
  return `/api/admin/users/${encodeURIComponent(userId)}/work`;
}

// ─────────── Hooks ───────────

export type AdminUsersState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; users: AdminUserSummary[]; refetch: () => void };

export function useAdminUsers(): AdminUsersState {
  const { apiFetch } = useApi();
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<AdminUsersState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    apiFetch<{ users: AdminUserSummary[] }>(adminUsersUrl())
      .then((body) => {
        if (!cancelled) {
          setState({ status: "ready", users: body.users, refetch: () => setTick((t) => t + 1) });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: "error", message: messageOf(err) });
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  return state;
}

export type AdminUserDetailState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; detail: AdminUserDetail; refetch: () => void };

export function useAdminUserDetail(userId: string): AdminUserDetailState {
  const { apiFetch } = useApi();
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<AdminUserDetailState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    apiFetch<AdminUserDetail>(adminUserDetailUrl(userId))
      .then((detail) => {
        if (!cancelled) setState({ status: "ready", detail, refetch: () => setTick((t) => t + 1) });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: "error", message: messageOf(err) });
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tick]);

  return state;
}

export type AdminUserWorkState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; items: AdminWorkItem[] };

export function useAdminUserWork(userId: string): AdminUserWorkState {
  const { apiFetch } = useApi();
  const [state, setState] = useState<AdminUserWorkState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    apiFetch<{ items: AdminWorkItem[] }>(adminUserWorkUrl(userId))
      .then((body) => {
        if (!cancelled) setState({ status: "ready", items: body.items });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: "error", message: messageOf(err) });
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return state;
}

// ─────────── Mutations ───────────

export async function resetProgress(
  apiFetch: ApiClient["apiFetch"],
  userId: string,
  courseSlug: string
): Promise<void> {
  await apiFetch(adminResetProgressUrl(userId, courseSlug), { method: "DELETE" });
}

export async function markOnboarded(
  apiFetch: ApiClient["apiFetch"],
  userId: string
): Promise<void> {
  await apiFetch(`/api/admin/users/${encodeURIComponent(userId)}/onboarding-complete`, {
    method: "POST"
  });
}

export async function resetOnboarding(
  apiFetch: ApiClient["apiFetch"],
  userId: string
): Promise<void> {
  await apiFetch(`/api/admin/users/${encodeURIComponent(userId)}/reset-onboarding?scope=portal`, {
    method: "POST"
  });
}

export async function saveCoachNote(
  apiFetch: ApiClient["apiFetch"],
  userId: string,
  note: { title: string; bodyMarkdown: string }
): Promise<void> {
  await apiFetch(`/api/admin/users/${encodeURIComponent(userId)}/coach-note`, {
    method: "PUT",
    body: JSON.stringify(note)
  });
}

export async function clearCoachNote(
  apiFetch: ApiClient["apiFetch"],
  userId: string
): Promise<void> {
  await apiFetch(`/api/admin/users/${encodeURIComponent(userId)}/coach-note`, {
    method: "DELETE"
  });
}

// ─────────── Course visibility ───────────

export interface AdminCourseSummary {
  slug: string;
  title: string;
  hidden: boolean;
  // CF-25b: review-cadence pair, present only for R2-published courses whose
  // course.yaml carries it (threaded through by the factory's
  // index-entry.json build step). Absent for static-base courses — the
  // admin UI shows no overdue badge for those, by design.
  lastReviewed?: string;
  reviewAfterMonths?: number;
}

export type AdminCoursesState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; courses: AdminCourseSummary[]; refetch: () => void };

export function useAdminCourses(): AdminCoursesState {
  const { apiFetch } = useApi();
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<AdminCoursesState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    apiFetch<{ courses: AdminCourseSummary[] }>("/api/admin/courses")
      .then((body) => {
        if (!cancelled) {
          setState({ status: "ready", courses: body.courses, refetch: () => setTick((t) => t + 1) });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: "error", message: messageOf(err) });
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  return state;
}

export async function setCourseVisibility(
  apiFetch: ApiClient["apiFetch"],
  slug: string,
  hidden: boolean
): Promise<void> {
  await apiFetch(`/api/admin/courses/${encodeURIComponent(slug)}/visibility`, {
    method: "PATCH",
    body: JSON.stringify({ hidden })
  });
}

// ─────────── Invitations ───────────

export interface InvitationRow {
  id: string;
  email: string;
  role: string;
  status: "pending" | "accepted" | "expired";
  createdAt: number;
  acceptedAt?: number; // set when the invite is accepted; absent for pending/expired
}

export type AdminInvitationsState =
  | { status: "loading" }
  | { status: "ready"; rows: InvitationRow[] }
  | { status: "error"; message: string };

export function useAdminInvitations(refreshKey: number): AdminInvitationsState {
  const { apiFetch } = useApi();
  const [state, setState] = useState<AdminInvitationsState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    apiFetch<{ invitations: InvitationRow[] }>("/api/admin/invitations?status=all")
      .then((d) => {
        if (!cancelled) setState({ status: "ready", rows: d.invitations });
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setState({
            status: "error",
            message: e instanceof ApiError ? e.message : "Failed to load invitations"
          });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  return state;
}

// ─────────── Per-user course onboarding (CF-23, Wave 4 Task 3) ───────────
// Types mirror functions/api/admin/users/[userId]/onboarding.ts's response
// shape exactly (server is the source of truth). That endpoint already
// existed with no frontend consumer prior to this task.

export interface AdminOnboardingResponseRow {
  fieldId: string;
  label: string;
  type: string;
  value: string | string[];
  presentInLiveSchema: boolean;
}

export type AdminOnboardingStatus = "complete" | "required-not-submitted" | "not-required";

export interface AdminUserOnboardingCourse {
  courseSlug: string;
  courseTitle: string;
  requireOnboarding: boolean;
  status: AdminOnboardingStatus;
  submittedAt: number | null;
  responses: AdminOnboardingResponseRow[];
}

export interface AdminUserOnboardingView {
  portal: {
    status: AdminOnboardingStatus;
    submittedAt: number | null;
    responses: AdminOnboardingResponseRow[];
  };
  courses: AdminUserOnboardingCourse[];
}

export type AdminUserOnboardingState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: AdminUserOnboardingView };

export function useAdminUserOnboarding(userId: string): AdminUserOnboardingState {
  const { apiFetch } = useApi();
  const [state, setState] = useState<AdminUserOnboardingState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    apiFetch<AdminUserOnboardingView>(`/api/admin/users/${encodeURIComponent(userId)}/onboarding`)
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: "error", message: messageOf(err) });
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return state;
}

// ─────────── Publish history (CF-19b, Wave 4 Task 5) ───────────
// Types mirror functions/api/admin/courses/[slug]/publish-history.ts's
// response shape, which in turn mirrors the factory's PublishRecord
// (functions/_lib/course-content.ts's read-side copy).

export interface AdminPublishRecord {
  version: string;
  channel: "draft" | "live" | "rollback";
  publishedAt: string;
  publishedBy: string;
  publishedByOs?: string;
  factoryVersion: string;
  note?: string;
  breakingDecision?: "accept" | "reset";
}

export interface AdminPublishHistory {
  slug: string;
  live: string | null;
  draft: string | null;
  history: AdminPublishRecord[];
}

export type AdminPublishHistoryState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: AdminPublishHistory };

export function adminPublishHistoryUrl(slug: string): string {
  return `/api/admin/courses/${encodeURIComponent(slug)}/publish-history`;
}

/**
 * Fetches on every mount — laziness comes from the caller only mounting the
 * component that uses this hook once a course row is expanded (see
 * AdminCourses.tsx's PublishHistoryPanel), not from any gating in here.
 */
export function useAdminPublishHistory(slug: string): AdminPublishHistoryState {
  const { apiFetch } = useApi();
  const [state, setState] = useState<AdminPublishHistoryState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    apiFetch<AdminPublishHistory>(adminPublishHistoryUrl(slug))
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: "error", message: messageOf(err) });
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return state;
}

// ─────────── Helpers ───────────

function messageOf(err: unknown): string {
  if (err instanceof ApiError) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
